import { endpoints } from '../config/endpoints';
import { apiFetch } from './api-client';
import type {
  Provider,
  ProviderTemplate,
  ProviderModel,
  CreateProviderRequest,
  UpdateProviderRequest,
  CustomModelFormData,
} from './provider-types';

/** Fetch all available provider templates (OpenAI, Anthropic, etc.). */
export async function fetchProviderTemplates(): Promise<ProviderTemplate[]> {
  const res = await apiFetch(endpoints.providerTemplates.list());
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.providersTemplates ?? data.data ?? [];
}

/** Fetch models for a given provider template. */
export async function fetchTemplateModels(providerId: number): Promise<ProviderModel[]> {
  const res = await apiFetch(endpoints.providerTemplates.models(String(providerId)));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.data ?? [];
}

/** Fetch all configured providers for a workspace. */
export async function fetchProviders(workspaceId: string): Promise<Provider[]> {
  const res = await apiFetch(endpoints.providers.list(workspaceId));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.data ?? [];
}

/** Create a new provider in a workspace. */
export async function createProvider(
  workspaceId: string,
  body: CreateProviderRequest,
): Promise<Provider> {
  const res = await apiFetch(endpoints.providers.create(workspaceId), {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.provider ?? data.data ?? data;
}

/** Update an existing provider. */
export async function updateProvider(
  workspaceId: string,
  providerId: number,
  body: UpdateProviderRequest,
): Promise<Provider> {
  const res = await apiFetch(endpoints.providers.update(workspaceId, providerId), {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.provider ?? data.data ?? data;
}

/** Delete a provider. */
export async function deleteProvider(
  workspaceId: string,
  providerId: number,
): Promise<void> {
  const res = await apiFetch(endpoints.providers.delete(workspaceId, providerId), {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
}

/** Fetch models for a configured workspace provider. */
export async function fetchProviderModels(
  workspaceId: string,
  providerId: number,
): Promise<ProviderModel[]> {
  const res = await apiFetch(endpoints.providers.models(workspaceId, providerId));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.data ?? [];
}

/** Create a custom model for a provider. */
export async function createCustomModel(
  workspaceId: string,
  providerId: number,
  body: CustomModelFormData,
): Promise<ProviderModel> {
  const res = await apiFetch(endpoints.providers.createCustomModel(workspaceId, providerId), {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.data ?? data;
}

/** Update a custom model. */
export async function updateCustomModel(
  workspaceId: string,
  providerId: number,
  modelId: number,
  body: Partial<CustomModelFormData>,
): Promise<ProviderModel> {
  const res = await apiFetch(endpoints.providers.updateCustomModel(workspaceId, providerId, modelId), {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.data ?? data;
}

/** Delete a custom model. */
export async function deleteCustomModel(
  workspaceId: string,
  providerId: number,
  modelId: number,
): Promise<void> {
  const res = await apiFetch(endpoints.providers.deleteCustomModel(workspaceId, providerId, modelId), {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `HTTP ${res.status}`);
  }
}
